import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, X, FileText, Image, File, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ACCEPT_ATTR, MAX_FILES, MAX_FILE_MB, addFiles, serverMessage, failedFiles } from "@/lib/uploadRules";

import { API, SENDING_ON } from "../lib/portalMode";
import PreviewNote from "./PreviewNote";
import DayFirstDate from "./DayFirstDate";

function getFileIcon(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  if (ext === 'pdf') return <FileText size={20} className="text-red-500" />;
  if (['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'bmp', 'tiff'].includes(ext)) return <Image size={20} className="text-blue-500" />;
  if (ext === 'docx') return <FileText size={20} className="text-blue-700" />;
  return <File size={20} className="text-gray-500" />;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUploadForm({ responses, setResponses, clientName, completedBy, date, onUploadComplete }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [refused, setRefused] = useState([]);   // iter 539: files that can't be sent, with the reason
  const [sent, setSent] = useState([]);         // the files in the last upload, in order (to retry the failed ones)

  const handleFileSelect = (selectedFiles) => {
    const { files: next, refused: no } = addFiles(files, selectedFiles);
    setFiles(next);
    setRefused(no);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!SENDING_ON) return; // off the live portal nothing is sent (lib/portalMode.js)
    if (files.length === 0) {
      toast.error("Please select at least one file to upload");
      return;
    }

    const name = responses.client_name || clientName || "";
    if (!name.trim()) {
      toast.error("Please enter the client name before uploading");
      return;
    }

    setUploading(true);
    setRefused([]);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('client_name', name);
      formData.append('upload_date', responses.upload_date || date || "");
      formData.append('notes', responses.notes || "");
      formData.append('completed_by', completedBy || "");

      const res = await axios.post(`${API}/assessments/upload-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      setSent(files);
      setUploadResult(res.data);
      if (res.data.failed) toast.warning(res.data.message);
      else toast.success(res.data.message);

      // Store upload info in responses for the parent component
      // Only the files that were saved count (a second batch adds to the first).
      const ok = (res.data.uploaded_files || []).filter(f => f.ok !== false);
      setResponses(prev => ({
        ...prev,
        uploaded_files: [...(prev.uploaded_files || []), ...ok],
        upload_record_id: res.data.record_id,
      }));

      if (onUploadComplete) {
        onUploadComplete(res.data);
      }
    } catch (error) {
      const data = error?.response?.data;
      if (data && Array.isArray(data.uploaded_files)) {
        // Nothing was saved: show each file's reason and keep them all ready to try again.
        setSent(files);
        setUploadResult(data);
      }
      toast.error(serverMessage(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="file-upload-form">
      {/* Client Name */}
      <div className="question-card" data-testid="file-upload-client-name">
        <Label className="question-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Client Name</Label>
        <Input
          value={responses.client_name || ""}
          onChange={(e) => setResponses(prev => ({ ...prev, client_name: e.target.value }))}
          placeholder="Enter client name"
          data-testid="file-upload-client-name-input"
        />
      </div>

      {/* Date */}
      <div className="question-card" data-testid="file-upload-date">
        <Label className="question-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Date</Label>
        <DayFirstDate
          label="Date"
          value={responses.upload_date || ""}
          onChange={(v) => setResponses(prev => ({ ...prev, upload_date: v }))}
          inputClassName="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm md:text-sm"
          testId="file-upload-date-input"
        />
      </div>

      {/* File Drop Zone */}
      <div className="question-card" data-testid="file-upload-dropzone-section">
        <Label className="question-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Upload Files</Label>
        <div
          data-testid="file-upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? '#2563EB' : '#cbd5e1'}`,
            borderRadius: '12px',
            padding: '32px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragOver ? 'rgba(37, 99, 235, 0.04)' : 'rgba(248, 250, 252, 0.5)',
            transition: 'all 0.2s ease',
          }}
        >
          <Upload size={32} style={{ margin: '0 auto 12px', color: '#94a3b8' }} />
          <p style={{ color: '#475569', fontWeight: 500, marginBottom: 4 }}>
            Drop files here or click to browse
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>
            PDF, photos (JPG, PNG, HEIC) and Word documents (DOCX)
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.8125rem' }} data-testid="file-upload-limits">
            Up to {MAX_FILES} files at a time, {MAX_FILE_MB} MB each
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
          style={{ display: 'none' }}
          data-testid="file-upload-input"
        />
      </div>

      {refused.length > 0 && (
        <div className="question-card" data-testid="file-upload-refused">
          {refused.map((r, i) => (
            <div key={i} data-testid={`file-upload-refused-${i}`}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 12px', backgroundColor: '#fef2f2',
                       borderRadius: 6, marginBottom: 4 }}>
              <AlertCircle size={16} style={{ color: '#b91c1c', flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: '0.8125rem', color: '#991b1b' }}>
                <strong>{r.name}</strong>: {r.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Selected Files List */}
      {files.length > 0 && (
        <div className="question-card" data-testid="file-upload-file-list">
          <Label className="question-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
            Selected Files ({files.length})
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {files.map((file, index) => (
              <div
                key={index}
                data-testid={`file-upload-file-item-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                {getFileIcon(file.name)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                  style={{
                    padding: 4,
                    borderRadius: 4,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8',
                  }}
                  data-testid={`file-upload-remove-${index}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="question-card" data-testid="file-upload-notes">
        <Label className="question-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Notes (optional)</Label>
        <textarea
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={responses.notes || ""}
          onChange={(e) => setResponses(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Any additional notes about these files..."
          rows={3}
          data-testid="file-upload-notes-input"
        />
      </div>

      {/* Upload Button */}
      {!uploadResult && (
        <div style={{ textAlign: 'center', paddingTop: 8 }}>
          <Button
            onClick={handleUpload}
            disabled={uploading || files.length === 0 || !SENDING_ON}
            size="lg"
            data-testid="file-upload-submit-btn"
            style={{
              minWidth: 200,
              backgroundColor: '#2563EB',
              color: 'white',
            }}
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading {files.length} file{files.length !== 1 ? 's' : ''}...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload {files.length} file{files.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
          <PreviewNote inline />
        </div>
      )}

      {/* Upload Success */}
      {uploadResult && (
        <Card data-testid="file-upload-success">
          <CardContent style={{ padding: 20, textAlign: 'center' }}>
            {uploadResult.success === false || uploadResult.failed
              ? <AlertCircle size={40} style={{ color: '#b91c1c', margin: '0 auto 12px' }} />
              : <CheckCircle2 size={40} style={{ color: '#16a34a', margin: '0 auto 12px' }} />}
            <h3 style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
              {uploadResult.success === false ? "Your files weren't saved" : uploadResult.failed ? "Some files didn't upload" : "Upload complete"}
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              {uploadResult.message}
            </p>
            <div style={{ marginTop: 16 }}>
              {uploadResult.uploaded_files?.map((f, i) => (
                <div key={i} data-testid={`file-upload-result-${i}`} data-ok={f.ok === false ? "no" : "yes"} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left',
                  padding: '8px 12px', backgroundColor: f.ok === false ? '#fef2f2' : '#f0fdf4', borderRadius: 6,
                  marginBottom: 4,
                }}>
                  {f.ok === false
                    ? <AlertCircle size={16} style={{ color: '#b91c1c', flexShrink: 0, marginTop: 2 }} />
                    : <CheckCircle2 size={16} style={{ color: '#16a34a', flexShrink: 0, marginTop: 2 }} />}
                  <span style={{ fontSize: '0.8125rem', color: f.ok === false ? '#991b1b' : '#166534' }}>
                    {f.original_filename}{f.ok === false && f.reason ? <>: {f.reason}</> : null}
                  </span>
                </div>
              ))}
            </div>
            {failedFiles(sent, uploadResult.uploaded_files).length > 0 && (
              <Button
                onClick={() => {
                  setFiles(failedFiles(sent, uploadResult.uploaded_files));
                  setUploadResult(null);
                }}
                size="sm"
                style={{ marginTop: 16, marginRight: 8, backgroundColor: '#2563EB', color: 'white' }}
                data-testid="file-upload-retry-btn"
              >
                Try the files in red again
              </Button>
            )}
            <Button
              onClick={() => {
                setFiles([]);
                setSent([]);
                setUploadResult(null);
              }}
              variant="outline"
              size="sm"
              style={{ marginTop: 16 }}
              data-testid="file-upload-another-btn"
            >
              Upload More Files
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
